import type { StampDutyRuleSet } from "./types.js";

// NSW transfer duty — 1 July 2025 – 30 June 2026.
//
// General transfer-duty scale transcribed VERBATIM from Revenue NSW
// "How to calculate transfer duty" (page last updated 29 May 2026, re-fetched
// clean 2026-06-23 — see stamp-duty-calculator-figures.md Fig 2/3):
//
//   $0          – $17,000     : $1.25 per $100 (min $20)
//   $17,001     – $37,000     : $212    + $1.50 per $100 over $17,000
//   $37,001     – $99,000     : $512    + $1.75 per $100 over $37,000
//   $99,001     – $372,000    : $1,597  + $3.50 per $100 over $99,000
//   $372,001    – $1,240,000  : $11,152 + $4.50 per $100 over $372,000
//   $1,240,001  – $3,721,000  : $50,212 + $5.50 per $100 over $1,240,000
//   $3,721,001  and over      : $186,667 + $7.00 per $100 over $3,721,000 (premium)
//
// Every base/over anchor above is the CITED RevNSW figure (Fig 2 records the
// $212 / $512 / $1,597 / $11,152 / $50,212-over-$1,240,000 chain verbatim; Fig 3
// confirms the premium "$186,667 + $7.00 per $100 over $3,721,000"). The
// intermediate thresholds reconcile the chain exactly and continuously:
//   $50,212 + 5.5% × ($3,721,000 − $1,240,000) = $186,667  (premium floor base)
// The boundary tests assert these CITED anchors directly, not the table's own
// bases back at themselves. Re-verify the CPI-indexed premium threshold before
// each FY rollover — never mutate this shipped file; author a new dated file.
//
// HISTORY: an earlier draft of this file encoded an invented schedule
// ($17,001/$36,001/$97,001/$364,001/$1,212,001/$3,636,001 with bases
// $212/$497/$1,564/$10,909/$49,069/$182,389) that diverged from the cited
// RevNSW table and over-stated premium-band duty by ~$1,672 at $3,721,000. That
// was a pre-first-ship authoring error, corrected here against the cited source
// (not a post-ship mutation of a live FY).
//
// Foreign purchaser surcharge duty: 9% from 1 January 2025 (was 8%) — 2024-25
// NSW Budget (Fig 4, CITED). Applies on top of general duty for foreign persons
// acquiring residential land.
//
// First Home Buyers Assistance Scheme (FHBAS): nil duty up to $800,000;
// concessional rate $800,000–$1,000,000; full duty at/above $1,000,000
// (liability date on/after 1 Jul 2023, current 2025-26 — first-home ledger
// Fig 1, CITED). Modelled as a linear fraction of full duty across the taper
// band (nsw-fhbas), boundary-tested against RevNSW.

export const nsw2025_26: StampDutyRuleSet = {
  state: "NSW",
  period: "2025-26",
  effectiveFrom: "2025-07-01",
  effectiveTo: "2026-06-30",
  sources: {
    transferDuty:
      "https://www.revenue.nsw.gov.au/taxes-duties-levies-royalties/transfer-duty/understanding-transfer-duty/calculate-transfer-duty",
    foreignSurcharge:
      "https://www.revenue.nsw.gov.au/taxes-duties-levies-royalties/transfer-duty/surcharge-purchaser-duty",
    firstHome: "https://www.revenue.nsw.gov.au/grants-schemes/assistance-scheme",
  },
  sourcesVerifiedAt: "2026-06-23",
  brackets: [
    { from: 0, to: 17_001, base: 0, rate: 0.0125, over: 0 },
    { from: 17_001, to: 37_001, base: 212, rate: 0.015, over: 17_000 },
    { from: 37_001, to: 99_001, base: 512, rate: 0.0175, over: 37_000 },
    { from: 99_001, to: 372_001, base: 1_597, rate: 0.035, over: 99_000 },
    { from: 372_001, to: 1_240_001, base: 11_152, rate: 0.045, over: 372_000 },
    { from: 1_240_001, to: 3_721_001, base: 50_212, rate: 0.055, over: 1_240_000 },
    // Premium duty bracket — 7% over the CPI-indexed premium threshold
    // ($3,721,000 for 1 Jul 2025–30 Jun 2026; base $186,667 — Fig 3, CITED).
    { from: 3_721_001, to: null, base: 186_667, rate: 0.07, over: 3_721_000 },
  ],
  foreignSurchargeRate: 0.09,
  foreignSurchargeFrom: "2025-01-01",
  firstHome: {
    fullExemptionUpTo: 800_000,
    taperEndsAt: 1_000_000,
    taper: "nsw-fhbas",
  },
  firstHomeNewBuild: null,
};
